import { useEffect, useRef, useState } from 'react';
import {
  ActionIcon,
  Alert,
  Button,
  Card,
  FileInput,
  Group,
  Image,
  MultiSelect,
  PasswordInput,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Textarea,
  Title
} from '@mantine/core';
import { IconX } from '@tabler/icons-react';

const API_URL = 'https://server.palyrobotics.com'; // Replace with your actual API URL
const DRIVETRAINS = [
  'Tank',
  'Omni',
  'Kit Swerve with Falcons/Krakens',
  'Kit Swerve with Non Falcons/Krakens',
  'Custom Swerve'
];
const SHUTTLE_OPTIONS = ['From neutral zone', 'From opposite side', 'No shuttling'];
const SHOOTERS = [
  'Single fixed shooter with no hood',
  'Single fixed shooter with hood',
  'Double shooter with no hood',
  'Double shooter with hood',
  'Turret with no hood',
  'Turret with hood',
  'Full width shooter with no hood',
  'Full width shooter with hood',
  'Other'
];
const INTAKE_OPTIONS = ['No intake', 'Depot', 'Ground', 'Human player station'];
const CLIMB_OPTIONS = ['No climb', 'L1', 'L2', 'L3', 'Auto climb'];

const emptyNotes = {
  playstyle: '',
  drivetrainType: '',
  robotDimensions: '',
  canDriveUnderTrench: '',
  canDriveOverBump: '',
  shuttlingCapabilities: [],
  robotWeight: '',
  ballCapacity: '',
  ballsPerSecond: '',
  shooterType: '',
  shooterOther: '',
  autoCapabilities: '',
  additionalNotes: '',
  intakeMethods: [],
  climbCapabilities: [],
  timeToClimb: '',
  driverRating: '',
  photos: []
};

const normalizeNotes = (notes) => ({
  ...emptyNotes,
  ...(notes && typeof notes === 'object' ? notes : {}),
  photos: Array.isArray(notes?.photos) ? notes.photos : []
});

const preparePhotoForUpload = async (file) => {
  if (file.size <= 1_250_000 || file.type === 'image/gif' || !window.createImageBitmap) return file;

  let bitmap;
  try {
    bitmap = await window.createImageBitmap(file);
    const scale = Math.min(1, 1600 / Math.max(bitmap.width, bitmap.height));
    if (scale === 1 && file.type !== 'image/heic' && file.type !== 'image/heif') return file;

    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));
    const context = canvas.getContext('2d');
    if (!context) return file;
    context.fillStyle = '#fff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);

    const jpeg = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.82));
    if (!jpeg || jpeg.size >= file.size) return file;

    const baseName = file.name.replace(/\.[^.]+$/, '');
    return new File([jpeg], `${baseName}.jpg`, {
      type: 'image/jpeg',
      lastModified: file.lastModified
    });
  } catch {
    return file;
  } finally {
    bitmap?.close();
  }
};

const TeamNotesCard = ({ event, team, notes, onSaved }) => {
  const [form, setForm] = useState(() => normalizeNotes(notes));
  const [editing, setEditing] = useState(false);
  const [password, setPassword] = useState('');
  const [token, setToken] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [removedPhotos, setRemovedPhotos] = useState([]);
  const [activeUploadIds, setActiveUploadIds] = useState(() => new Set());
  const [otherEditorActive, setOtherEditorActive] = useState(false);
  const uploadControllers = useRef(new Map());
  const nextPhotoId = useRef(0);
  const editorId = useRef(window.crypto?.randomUUID
    ? window.crypto.randomUUID()
    : `editor-${Date.now()}-${Math.random().toString(36).slice(2)}`);

  const editorHeaders = (currentToken) => ({
    Authorization: `Bearer ${currentToken}`,
    'X-Notes-Editor-Id': editorId.current
  });

  useEffect(() => {
    setForm(normalizeNotes(notes));
    setEditing(false);
    setToken('');
    setError('');
    setMessage('');
    setSelectedPhotos([]);
    setRemovedPhotos([]);
    uploadControllers.current.forEach((controller) => controller.abort());
    uploadControllers.current.clear();
    setActiveUploadIds(new Set());
  }, [event, team, notes]);

  useEffect(() => {
    let active = true;
    const checkEditor = async () => {
      try {
        const response = await fetch(`${API_URL}/team/${event}/${team}/notes/edit-status?editorId=${encodeURIComponent(editorId.current)}`);
        if (!response.ok) return;
        const status = await response.json();
        if (active) setOtherEditorActive(status.editing && !status.ownedByMe);
      } catch {
        if (active) setOtherEditorActive(false);
      }
    };

    checkEditor();
    const interval = window.setInterval(checkEditor, 8000);
    return () => {
      active = false;
      window.clearInterval(interval);
    };
  }, [event, team]);

  useEffect(() => {
    if (!editing || !token) return undefined;

    const renewLease = async () => {
      try {
        const response = await fetch(`${API_URL}/team/${event}/${team}/notes/edit-lock`, {
          method: 'POST',
          headers: { ...editorHeaders(token), 'Content-Type': 'application/json' },
          body: JSON.stringify({ editorId: editorId.current })
        });
        if (response.status === 409) {
          setEditing(false);
          setToken('');
          setError('Your editing lock expired because another scout started editing this profile.');
        }
      } catch (requestError) {
        console.error('Unable to renew profile editing lock:', requestError);
      }
    };

    const interval = window.setInterval(renewLease, 15000);
    return () => window.clearInterval(interval);
  }, [editing, token, event, team]);

  const releaseLease = async (currentToken = token) => {
    if (!currentToken) return;
    try {
      await fetch(`${API_URL}/team/${event}/${team}/notes/edit-lock`, {
        method: 'DELETE',
        headers: editorHeaders(currentToken)
      });
    } catch (requestError) {
      console.error('Unable to release profile editing lock:', requestError);
    }
  };

  const setField = (field, value) => setForm((current) => ({ ...current, [field]: value ?? '' }));
  const selectData = (items) => items.map((value) => ({ value, label: value }));

  const unlock = async () => {
    setError('');
    setMessage('');
    try {
      const response = await fetch(`${API_URL}/team/notes/unlock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Unable to unlock pit-scout profile editing.');
      const lockResponse = await fetch(`${API_URL}/team/${event}/${team}/notes/edit-lock`, {
        method: 'POST',
        headers: {
          ...editorHeaders(data.token),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ editorId: editorId.current })
      });
      const lockData = await lockResponse.json();
      if (!lockResponse.ok) throw new Error(lockData.error || 'Another scout is editing this profile.');
      setToken(data.token);
      setForm(normalizeNotes(notes));
      setEditing(true);
      setPassword('');
    } catch (requestError) {
      setError(requestError.message);
    }
  };

  const save = async () => {
    setSaving(true);
    let nextNotes = form;
    setError('');
    setMessage('');
    try {
      const uploadResults = [];
      const uploadFailures = [];
      const uploadedIds = new Set();
      let uploadIndex = 0;

      const uploadWorker = async () => {
        while (uploadIndex < selectedPhotos.length) {
          const selectedPhoto = selectedPhotos[uploadIndex++];
          const controller = new AbortController();
          uploadControllers.current.set(selectedPhoto.id, controller);
          setActiveUploadIds((current) => new Set(current).add(selectedPhoto.id));
          try {
            const uploadFile = await preparePhotoForUpload(selectedPhoto.file);
            if (controller.signal.aborted) continue;
            const uploadBody = new FormData();
            uploadBody.append('photos', uploadFile);
            const uploadResponse = await fetch(`${API_URL}/team/${event}/${team}/notes/photos`, {
              method: 'POST',
              headers: editorHeaders(token),
              body: uploadBody,
              signal: controller.signal
            });
            const uploadData = await uploadResponse.json();
            if (!uploadResponse.ok) throw new Error(uploadData.error || 'Unable to upload photo.');
            uploadResults.push(...uploadData.photos);
            uploadedIds.add(selectedPhoto.id);
          } catch (uploadError) {
            if (uploadError.name !== 'AbortError') {
              uploadFailures.push(`${selectedPhoto.file.name}: ${uploadError.message}`);
            }
          } finally {
            uploadControllers.current.delete(selectedPhoto.id);
            setActiveUploadIds((current) => {
              const updated = new Set(current);
              updated.delete(selectedPhoto.id);
              return updated;
            });
          }
        }
      };

      await Promise.all(
        Array.from({ length: Math.min(3, selectedPhotos.length) }, () => uploadWorker())
      );
      if (uploadResults.length) {
        nextNotes = { ...nextNotes, photos: [...nextNotes.photos, ...uploadResults] };
        setForm(nextNotes);
        setSelectedPhotos((current) => current.filter((photo) => !uploadedIds.has(photo.id)));
      }

      const response = await fetch(`${API_URL}/team/${event}/${team}/notes`, {
        method: 'PUT',
        headers: {
          ...editorHeaders(token),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(nextNotes)
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || data.details?.join(' ') || 'Unable to save team profile.');
      setForm(normalizeNotes(data.notes));
      onSaved(data.notes);
      setRemovedPhotos([]);
      let saveMessage = 'Profile saved.';
      if (removedPhotos.length) {
        try {
          const deleteResponse = await fetch(`${API_URL}/team/${event}/${team}/notes/photos`, {
            method: 'DELETE',
            headers: {
              ...editorHeaders(token),
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ paths: removedPhotos.map((photo) => photo.path) })
          });
          if (!deleteResponse.ok) {
            saveMessage = 'Profile saved, but one or more removed photos could not be deleted from storage.';
          }
        } catch {
          saveMessage = 'Profile saved, but photo cleanup could not reach the server.';
        }
      }
      setMessage(saveMessage);
      if (uploadFailures.length) {
        setError(`${uploadFailures.length} photo(s) failed to upload and remain selected. Retry when the connection is stronger.`);
      }
      await releaseLease(token);
      setEditing(false);
      setToken('');
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    uploadControllers.current.forEach((controller) => controller.abort());
    uploadControllers.current.clear();
    releaseLease();
    setForm(normalizeNotes(notes));
    setEditing(false);
    setToken('');
    setSelectedPhotos([]);
    setRemovedPhotos([]);
    setActiveUploadIds(new Set());
    setError('');
  };

  return (
    <Card withBorder>
      <Stack spacing="md">
        <Group position="apart" align="center">
          <div>
            <Title order={3}>Pit Scout Profile</Title>
          </div>
        </Group>

        {error && <Alert color="red">{error}</Alert>}
        {message && <Alert color="green">{message}</Alert>}
        {otherEditorActive && (
          <Alert color="yellow" title="Profile currently being edited">
            Another scout has this team profile open for editing. Saving edits from another session is temporarily locked.
          </Alert>
        )}

        {!editing && (
          <>
            <Group align="end">
              <PasswordInput
                label="Enter pit-scouting password:"
                value={password}
                onChange={(eventValue) => setPassword(eventValue.currentTarget.value)}
                onKeyDown={(keyboardEvent) => keyboardEvent.key === 'Enter' && unlock()}
                style={{ flex: 1 }}
              />
              <Button onClick={unlock} disabled={!password || otherEditorActive}>Unlock</Button>
            </Group>
            <SimpleGrid cols={2} breakpoints={[{ maxWidth: 'sm', cols: 1 }]}>
              <Text><b>Playstyle:</b> {form.playstyle || 'Not recorded'}</Text>
              <Text><b>Drivetrain:</b> {form.drivetrainType || 'Not recorded'}</Text>
              <Text><b>Dimensions:</b> {form.robotDimensions || 'Not recorded'}</Text>
              <Text><b>Can drive under trench:</b> {form.canDriveUnderTrench || 'Not recorded'}</Text>
              <Text><b>Can drive over bump:</b> {form.canDriveOverBump || 'Not recorded'}</Text>
              <Text><b>Shuttling capabilities:</b> {form.shuttlingCapabilities.join(', ') || 'Not recorded'}</Text>
              <Text><b>Auto:</b> {form.autoCapabilities || 'Not recorded'}</Text>
              <Text><b>Robot Weight:</b> {form.robotWeight || 'Not recorded'}</Text>
              <Text><b>Estimated ball capacity:</b> {form.ballCapacity || 'Not recorded'}</Text>
              <Text><b>Estimated balls per second:</b> {form.ballsPerSecond || 'Not recorded'}</Text>
              <Text><b>Shooter type:</b> {form.shooterType === 'Other' ? form.shooterOther || 'Other' : form.shooterType || 'Not recorded'}</Text>
              <Text><b>Intake methods:</b> {form.intakeMethods.join(', ') || 'Not recorded'}</Text>
              <Text><b>Climb capabilities:</b> {form.climbCapabilities.join(', ') || 'Not recorded'}</Text>
              <Text><b>Time to climb:</b> {form.timeToClimb || 'Not recorded'}</Text>
              <Text><b>Driver rating:</b> {form.driverRating || 'Not recorded'}</Text>
              <Text><b>Additional notes:</b> {form.additionalNotes || 'Not recorded'}</Text>
            </SimpleGrid>
          </>
        )}

        {editing && (
          <>
            <SimpleGrid cols={2} breakpoints={[{ maxWidth: 'sm', cols: 1 }]}>
              <Textarea label="Playstyle (scoring defense, ...)" value={form.playstyle} onChange={(eventValue) => setField('playstyle', eventValue.currentTarget.value)} />
              <Select label="Drivetrain type" clearable data={selectData(DRIVETRAINS)} value={form.drivetrainType || null} onChange={(value) => setField('drivetrainType', value)} />
              <TextInput label="Robot dimensions" value={form.robotDimensions} onChange={(eventValue) => setField('robotDimensions', eventValue.currentTarget.value)} />
              <Select label="Can drive under trench?" clearable data={['yes', 'no']} value={form.canDriveUnderTrench || null} onChange={(value) => setField('canDriveUnderTrench', value)} />
              <Select label="Can drive over bump?" clearable data={['yes', 'no']} value={form.canDriveOverBump || null} onChange={(value) => setField('canDriveOverBump', value)} />
              <MultiSelect label="Shuttling capabilities" data={selectData(SHUTTLE_OPTIONS)} value={form.shuttlingCapabilities} onChange={(value) => setField('shuttlingCapabilities', value)} />
              <Textarea label="Auto capabilities" value={form.autoCapabilities} onChange={(eventValue) => setField('autoCapabilities', eventValue.currentTarget.value)} />
              <TextInput label="Robot weight" value={form.robotWeight} onChange={(eventValue) => setField('robotWeight', eventValue.currentTarget.value)} />
              <TextInput label="Estimated ball capacity" value={form.ballCapacity} onChange={(eventValue) => setField('ballCapacity', eventValue.currentTarget.value)} />
              <TextInput label="Estimated balls per second" value={form.ballsPerSecond} onChange={(eventValue) => setField('ballsPerSecond', eventValue.currentTarget.value)} />
              <Select label="Shooter type" clearable data={selectData(SHOOTERS)} value={form.shooterType || null} onChange={(value) => setField('shooterType', value)} />
              {form.shooterType === 'Other' && <TextInput placeholder="Shooter type" value={form.shooterOther} onChange={(eventValue) => setField('shooterOther', eventValue.currentTarget.value)} />}
              <MultiSelect label="Intake methods" data={selectData(INTAKE_OPTIONS)} value={form.intakeMethods} onChange={(value) => setField('intakeMethods', value)} />
              <MultiSelect label="Climb capabilities" data={selectData(CLIMB_OPTIONS)} value={form.climbCapabilities} onChange={(value) => setField('climbCapabilities', value)} />
              <TextInput label="Time to climb" value={form.timeToClimb} onChange={(eventValue) => setField('timeToClimb', eventValue.currentTarget.value)} />
              <Select label="Driver rating (If you were standing in the middle of the field, how confident are you that your driver wouldn't hit you? 1-10)" clearable data={Array.from({ length: 10 }, (_, index) => String(index + 1))} value={form.driverRating ? String(form.driverRating) : null} onChange={(value) => setField('driverRating', value || '')} />
              <Textarea label="Any additional notes" value={form.additionalNotes} onChange={(eventValue) => setField('additionalNotes', eventValue.currentTarget.value)} />
            </SimpleGrid>

            <Alert color="yellow" title="Photos">
              Add up to 20 photos. Images are uploaded securely when you save the profile.
            </Alert>
            <FileInput
              label="Robot photos"
              placeholder="Choose photos"
              accept="image/jpeg,image/png,image/webp,image/gif,image/heic,image/heif,.heic,.heif"
              multiple
              disabled={saving}
              value={selectedPhotos.map((photo) => photo.file)}
              onChange={(files) => {
                const existingNames = new Set(selectedPhotos.map((photo) => photo.file.name));
                const newFiles = files.filter((file) => !existingNames.has(file.name));
                if (form.photos.length + selectedPhotos.length + newFiles.length > 20) {
                  setError('A team profile can have at most 20 photos.');
                  return;
                }
                setError('');
                setSelectedPhotos((current) => [
                  ...current,
                  ...newFiles.map((file) => ({ id: `photo-${nextPhotoId.current++}`, file }))
                ]);
              }}
            />
            {selectedPhotos.length > 0 && (
              <Stack spacing="xs">
                {selectedPhotos.map((photo) => (
                  <Group key={photo.id} position="apart" noWrap>
                    <Text size="sm" style={{ overflowWrap: 'anywhere' }}>
                      {photo.file.name}{activeUploadIds.has(photo.id) ? ' (preparing/uploading...)' : ''}
                    </Text>
                    <ActionIcon
                      aria-label={`Cancel upload of ${photo.file.name}`}
                      title="Remove or cancel upload"
                      color="red"
                      variant="subtle"
                      onClick={() => {
                        uploadControllers.current.get(photo.id)?.abort();
                        setSelectedPhotos((current) => current.filter((item) => item.id !== photo.id));
                      }}
                    >
                      <IconX size={16} />
                    </ActionIcon>
                  </Group>
                ))}
              </Stack>
            )}
            {form.photos.length > 0 && (
              <SimpleGrid cols={3} breakpoints={[{ maxWidth: 'sm', cols: 1 }]}>
                {form.photos.map((photo) => (
                  <Card key={photo.path || photo.url} withBorder p="xs">
                    <Image src={photo.url} alt={photo.name || 'Team robot'} caption={photo.name} height={160} fit="contain" />
                    <Button
                      color="red"
                      variant="subtle"
                      size="xs"
                      mt="xs"
                      onClick={() => {
                        setForm((current) => ({
                          ...current,
                          photos: current.photos.filter((currentPhoto) => currentPhoto.path !== photo.path)
                        }));
                        setRemovedPhotos((current) => [...current, photo]);
                      }}
                    >
                      Remove photo
                    </Button>
                  </Card>
                ))}
              </SimpleGrid>
            )}

            <Group position="right">
              <Button variant="default" onClick={cancel} disabled={saving}>Cancel</Button>
              <Button onClick={save} loading={saving}>Save profile</Button>
            </Group>
          </>
        )}
      </Stack>
    </Card>
  );
};

export default TeamNotesCard;